// Utility hook used to retrieve the styles of React-Select components for light and dark themes.
import { useCallback, useMemo } from 'react'
import { GroupBase, StylesConfig } from 'react-select'
import defaultTheme from 'tailwindcss/defaultTheme'
import resolveConfig from 'tailwindcss/resolveConfig'

import * as tailwindConfig from '../../tailwind.config'
import { useDetectTheme } from '../hooks'

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const tailwind = resolveConfig(tailwindConfig)

const useSelectStyles = <
  T,
  IsMulti extends boolean,
  Group extends GroupBase<T> = GroupBase<T>,
>({
  isAddon,
  isInvalid,
}: {
  isInvalid?: boolean
  isAddon?: boolean
}) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const themeColors = (tailwind.theme || defaultTheme).colors as any
  const theme = useDetectTheme()

  const getThemedBorderColor = useCallback(
    () => (theme === 'dark' ? themeColors.gray[600] : themeColors.gray[300]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme],
  )
  const getThemedFocusedBackgroundColor = useCallback(
    () => (theme === 'dark' ? themeColors.gray[800] : themeColors.gray[200]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme],
  )
  const getThemedBackgroundColor = useCallback(
    () => (theme === 'dark' ? themeColors.black : themeColors.white),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme],
  )
  const getThemedTextColor = useCallback(
    () => (theme === 'dark' ? themeColors.white : themeColors.gray[900]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme],
  )
  const getThemedIndicatorColor = useCallback(
    () => (theme === 'dark' ? themeColors.gray[400] : themeColors.gray[900]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme],
  )

  const styles: StylesConfig<T, IsMulti, Group> = useMemo(
    () => ({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      control: (provided: Record<string, unknown>, state: any) => {
        const getBorderColor = () => {
          if (isInvalid) {
            return themeColors.red[500]
          }

          if (state.isFocused) {
            return themeColors['primary'][500]
          }

          return isAddon ? themeColors.transparent : getThemedBorderColor()
        }

        return {
          ...provided,
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: getBorderColor(),
          backgroundColor: getThemedBackgroundColor(),
          minHeight: 'unset',
          padding: '6px',
          '&:hover': {
            borderColor: getBorderColor(),
            borderRightColor: isInvalid
              ? themeColors.red[500]
              : getThemedBorderColor(),
            boxShadow: `0 0 0 1px ${getBorderColor()}`,
          },
          color: getThemedTextColor(),
          ...(isAddon
            ? {
                borderBottomRightRadius: '0px',
                borderTopRightRadius: '0px',
                borderRightColor: isInvalid
                  ? themeColors.red[500]
                  : getThemedBorderColor(),
              }
            : {}),
        }
      },
      indicatorsContainer: (provided: Record<string, unknown>) => ({
        ...provided,
        height: '32px',
      }),
      dropdownIndicator: (provided: Record<string, unknown>) => ({
        ...provided,
        color: getThemedIndicatorColor(),
      }),
      singleValue: (provided: Record<string, unknown>) => ({
        ...provided,
        color: getThemedTextColor(),
      }),
      multiValue: (provided: Record<string, unknown>) => ({
        ...provided,
        color: getThemedTextColor(),
      }),
      input: (provided: Record<string, unknown>) => ({
        ...provided,
        color: getThemedTextColor(),
      }),
      indicatorSeparator: (provided: Record<string, unknown>) => ({
        ...provided,
        color: getThemedIndicatorColor(),
      }),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      option: (provided: Record<string, unknown>, state: any) => {
        return {
          ...provided,
          backgroundColor: state.isFocused
            ? getThemedFocusedBackgroundColor()
            : getThemedBackgroundColor(),
          color: getThemedTextColor(),
          fontWeight: state.isSelected ? 500 : 400,
        }
      },
      menu: (provided) => ({
        ...provided,
        backgroundColor: getThemedBackgroundColor(),
        borderColor: getThemedBorderColor(),
        borderWidth: '1px',
        borderStyle: 'solid',
        borderRadius: '5px',
      }),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isInvalid, themeColors, isAddon, theme],
  )

  return styles
}

export default useSelectStyles
